import { useState, useEffect, useCallback } from "react";
import {
  Fuel,
  Wheat,
  Mountain,
  FlaskConical,
  TreePine,
  Gem,
  ArrowRight,
} from "lucide-react";
import verticalPetrochem from "../../assets/img/verticals/vertical-petrochem.jpg";
import verticalAgro from "../../assets/img/verticals/vertical-agro.jpg";
import verticalMinerals from "../../assets/img/verticals/vertical-minerals.jpg";
import verticalChemicals from "../../assets/img/verticals/vertical-chemicals.jpg";
import verticalWood from "../../assets/img/verticals/vertical-wood.jpg";
import verticalSpecialty from "../../assets/img/verticals/vertical-specialty.jpg";

const verticals = [
  {
    icon: Fuel,
    title: "Petrochemicals",
    description:
      "Polymers, base oils, bitumen, solvents, and petroleum derivatives sourced from certified refineries worldwide.",
    products: [
      "Pet Coke",
      "HDPE / LDPE / LLDPE",
      "Polypropylene",
      "Base Oils",
      "Bitumen",
    ],
    image: verticalPetrochem,
  },
  {
    icon: Wheat,
    title: "Agro Commodities",
    description:
      "Premium rice, pulses, spices, dehydrated onion, sugar, and oilseeds — farm-direct sourcing with full traceability.",
    products: [
      "Basmati & Non-Basmati Rice",
      "Pulses & Lentils",
      "Spices",
      "Dehydrated Onion",
      "Sugar & Oilseeds",
    ],
    image: verticalAgro,
  },
  {
    icon: Mountain,
    title: "Minerals & Ores",
    description:
      "Iron ore, manganese, bauxite, limestone, and industrial minerals from verified mining operations.",
    products: ["Iron Ore", "Manganese", "Bauxite", "Limestone", "Silica Sand"],
    image: verticalMinerals,
  },
  {
    icon: FlaskConical,
    title: "Chemicals",
    description:
      "Industrial and specialty chemicals — caustic soda, acids, dyes, and intermediates with REACH compliance.",
    products: [
      "Caustic Soda",
      "Sulphuric Acid",
      "Dyes & Pigments",
      "Soda Ash",
      "Chemical Intermediates",
    ],
    image: verticalChemicals,
  },
  {
    icon: TreePine,
    title: "Wood & Timber",
    description:
      "Hardwood, softwood, plywood, and timber products from sustainably managed forests with FSC certification.",
    products: [
      "Teak & Hardwood",
      "Pine & Softwood",
      "Plywood",
      "MDF / Particle Board",
      "Wood Pellets",
    ],
    image: verticalWood,
  },
  {
    icon: Gem,
    title: "Specialty & More",
    description:
      "Textiles, construction materials, packaging, and other commodities — if you need it sourced, we can procure it.",
    products: [
      "Textiles & Fabrics",
      "Steel & Metals",
      "Cement & Clinker",
      "Packaging Materials",
      "Custom Sourcing",
    ],
    image: verticalSpecialty,
  },
];

const CYCLE_INTERVAL = 3500;

export default function VerticalsSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % verticals.length);
  }, []);

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(next, CYCLE_INTERVAL);
    return () => clearInterval(timer);
  }, [paused, next]);

  const handleClick = (i: number) => {
    setActiveIndex(i);
    setPaused(true);
    setTimeout(() => setPaused(false), 8000);
  };

  const handleMouseEnter = (i: number) => {
    setActiveIndex(i);
    setPaused(true);
  };

  const handleMouseLeave = () => {
    setPaused(false);
  };

  return (
    <section id="what" className="py-10 md:py-16">
      <div className="container px-4">
        <div className="max-w-2xl mb-12">
          <p className="text-sm font-semibold text-primary mb-3 tracking-wide uppercase">
            WHAT WE PROCURE
          </p>
          <h2 className="text-3xl md:text-[2.75rem] font-display font-bold text-foreground leading-tight tracking-tight">
            Six verticals.
            <br />
            <span className="text-gradient">One procurement partner.</span>
          </h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            From raw materials to finished commodities — we source, negotiate,
            and deliver across all major trade verticals.
          </p>
        </div>

        {/* Expanding cards */}
        <div className="flex flex-col md:flex-row gap-2 md:h-[520px]">
          {verticals.map((v, i) => {
            const isActive = activeIndex === i;

            return (
              <div
                key={v.title}
                className={`
                  relative rounded-2xl overflow-hidden cursor-pointer
                  transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]
                  ${isActive ? "md:flex-[3.5]" : "md:flex-[0.5]"}
                  ${
                    isActive
                      ? "min-h-[360px] md:min-h-0"
                      : "min-h-[72px] md:min-h-0"
                  }
                `}
                onClick={() => handleClick(i)}
                onMouseEnter={() => handleMouseEnter(i)}
                onMouseLeave={handleMouseLeave}
              >
                {/* Background image */}
                <img
                  src={v.image}
                  alt={v.title}
                  className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ${
                    isActive ? "scale-105 opacity-100" : "opacity-0 scale-100"
                  }`}
                />

                {/* Overlay */}
                <div
                  className={`absolute inset-0 transition-all duration-500 ${
                    isActive
                      ? "bg-foreground/60"
                      : "bg-gradient-to-b from-primary/20 via-primary/10 to-primary/5 border border-primary/15"
                  }`}
                />

                {/* Collapsed state */}
                <div
                  className={`
                    absolute inset-0 flex items-center transition-opacity duration-300 z-10
                    ${isActive ? "opacity-0 pointer-events-none" : "opacity-100"}
                  `}
                >
                  <div className="flex md:flex-col items-center md:items-start gap-3 md:gap-4 px-6 py-5 md:py-0 md:justify-center md:h-full w-full">
                    <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center">
                      <v.icon className="w-4.5 h-4.5 text-primary" />
                    </div>
                    <span className="text-sm font-display font-bold text-foreground md:[writing-mode:vertical-lr] md:rotate-180">
                      {v.title}
                    </span>
                  </div>
                </div>

                {/* Expanded state */}
                <div
                  className={`
                    absolute inset-0 flex flex-col justify-end p-8 md:p-10 z-10
                    transition-opacity duration-500
                    ${isActive ? "opacity-100" : "opacity-0 pointer-events-none"}
                  `}
                >
                  <v.icon className="w-8 h-8 text-primary-foreground/70 mb-4" />
                  <h3 className="text-2xl font-display font-bold text-primary-foreground mb-2 tracking-tight">
                    {v.title}
                  </h3>
                  <p className="text-sm text-primary-foreground/70 leading-relaxed mb-5 max-w-sm">
                    {v.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {v.products.map((p) => (
                      <span
                        key={p}
                        className="px-3 py-1 rounded-full text-xs font-medium bg-primary-foreground/15 text-primary-foreground/90 border border-primary-foreground/10 backdrop-blur-sm"
                      >
                        {p}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Progress bar */}
                {isActive && !paused && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-foreground/10 z-20">
                    <div
                      className="h-full bg-primary-foreground/50 rounded-full"
                      style={{
                        animation: `progress ${CYCLE_INTERVAL}ms linear`,
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-10 text-center">
          <a
            href="#contact"
            className="inline-flex items-center gap-2 px-7 py-3 text-sm font-bold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Request a Quote <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>

      <style>{`
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </section>
  );
}
